import { forwardRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

// Deep imports on purpose: the vendored barrel doesn't export the scene
// surface (tapfly's own hosts deep-import SceneCanvas the same way).
import SceneCanvas, { useSceneComposition } from "../vendor/liveTapRenderer/components/SceneCanvas";
import { createSymbolImageClient } from "../vendor/liveTapRenderer/lib/symbolImageClient";
// The pen-trace/seen-reveal keyframes CuratedText leans on. The classic path
// gets them via TapestryCanvas's own import; the scene view carries its own so
// it never depends on the classic canvas having been bundled first.
import "../vendor/liveTapRenderer/styles/tapestryAnimations.css";
import useSceneBoardPoll, { costUsd } from "../liveTap/useSceneBoardPoll";
import useTranscriptFocus from "../liveTap/useTranscriptFocus";
import useArtManifest from "../liveTap/useArtManifest";
import { PaperWait, StyleMismatch, isStyleMismatch } from "./LiveBoardStates";
import ClassicOpening from "./ClassicOpening";
import FullscreenStage from "./FullscreenStage";
import LiveHandoff from "./LiveHandoff";
import LiveTapInfoPanel from "./LiveTapInfoPanel";
import SplitStage from "./SplitStage";
import styles from "./SceneLiveView.module.scss";


/**
 * The "scene" live system (TT-1046): renders the server-built graphic
 * recording board for a live doc — watercolor metaphor art + banners — in
 * place of the classic tapestry canvas.
 *
 * The poll/advance cycle lives in useSceneBoardPoll (shared with the board
 * system). The board spec is rendered whole (currentSent=Infinity):
 * reveal-on-arrival is the reveal schedule's job, not the playhead's. On a
 * closed doc the intros are skipped (`instant`) — a finished meeting
 * should read at a glance, not re-letter itself for a minute.
 *
 * Until the planner's first version lands, the opening IS classic (TT-1133,
 * Ben's second-run note): the classic canvas itself — symbol roles, slot
 * fill, dwell, the pen — on its paper skin, with its layout catalog off so
 * it stays generative (the metaphor is the planner's call, never the
 * catalog's); it fades out over the planned board when that lands, and
 * unmounts when its fade ends. Never a "wait for content" message, and no
 * chip about the board's state either (TT-1168 note 1). The host page owns
 * the playhead (`currentSent`) and the symbol threshold, as it does for
 * classic.
 *
 * What classic has, the scene has too (TT-1168 note 5): the transcript
 * panel beside the board (`infoOpen`, the host's toggle) — in a gutter at
 * the right edge while fullscreen, as classic shows it (TT-1174 note 3) —
 * the canvas's own PNG export through the ref (`exportPng()`, the classic
 * contract), and context on a click: every quote on the board (the
 * sentence as spoken; a paraphrase is not a link) opens the panel on that
 * sentence, highlighted in its turn. The panel then holds there — the
 * playhead moving must not drag it back to the live end (TT-1174 note 5)
 * — until "Back to live" or the panel is closed.
 */
const SceneLiveView = forwardRef(function SceneLiveView(
  {
    feed,
    resources,
    docTitle,
    docDate,
    segments,
    currentSent,
    currentTurn,
    threshold = 0.5,
    closed = false,
    debug = false,
    infoOpen = false,
    onInfoOpenChange,
    onBoard,
  },
  ref
) {
  const { board, job, jobError, fetchError } = useSceneBoardPoll(feed, "story", {
    errorFallback: "The scene builder hit an error",
    closed,
  });

  useEffect(() => onBoard?.(board), [board, onBoard]);
  const mismatch = isStyleMismatch(board, "scene");
  const planned = (!mismatch && board?.spec) || null;
  // Same client the classic canvas builds from the appback resource adapter —
  // the scene's spot icons are the same symbol SVG library.
  const imageClient = useMemo(() => createSymbolImageClient(resources), [resources]);
  // The scene's backdrops and mids, from the catalog (TT-1310).
  const sceneArt = useArtManifest(resources, "scene");
  // 'Patrick Hand' is only the fallback face; specs normally carry their own
  // fonts ({ caps, script }) and those win inside the solver.
  const [composition, , assetError] = useSceneComposition(planned, {
    currentSent: Infinity,
    fontFamily: "Patrick Hand",
    manifest: sceneArt,
  });
  const assetMsg = assetError ? `The scene artwork failed to load (${assetError.message}).` : "";

  // The opening canvas runs while there is transcript and no planned board,
  // then fades over the board (LiveHandoff).
  const boardUp = Boolean(planned && composition);
  const opening = Boolean(segments?.length) && !mismatch;

  const { focus, onRelease, onSentClick } = useTranscriptFocus(infoOpen, onInfoOpenChange);
  const panel = <LiveTapInfoPanel segments={segments} currentTurn={currentTurn} focus={focus} onRelease={onRelease} />;

  return (
    <div className={styles.sceneStage}>
      {(jobError || fetchError || assetMsg) && (
        <div className={styles.errorBanner}>{jobError || fetchError || assetMsg}</div>
      )}
      {mismatch && <StyleMismatch archetype={board.archetype} />}
      {planned || opening ? (
        <SplitStage infoOpen={infoOpen} panel={panel}>
          {({ aside, onFullscreenChange }) => (
            <FullscreenStage aside={aside} onChange={onFullscreenChange}>
              <LiveHandoff
                up={boardUp}
                opening={
                  opening ? (
                    <ClassicOpening
                      segments={segments}
                      currentSent={currentSent}
                      threshold={threshold}
                      docTitle={docTitle}
                      docDate={docDate}
                      resources={resources}
                      imageClient={imageClient}
                    />
                  ) : null
                }
              >
                {planned && (
                  <SceneCanvas
                    ref={ref}
                    composition={composition}
                    meetingName={docTitle}
                    meetingDate={docDate}
                    currentSent={Infinity}
                    camera="fit"
                    instant={closed}
                    imageClient={imageClient}
                    onSentClick={onSentClick}
                    style={{
                      maxWidth: `min(100%, calc(var(--stage-cap, calc(100vh - 220px)) * ${composition?.aspect || 16 / 9}))`,
                      margin: "0 auto",
                    }}
                  />
                )}
              </LiveHandoff>
            </FullscreenStage>
          )}
        </SplitStage>
      ) : (
        !mismatch && <PaperWait title={docTitle} hint={closed ? "No scene board was drawn for this meeting." : undefined} />
      )}
      {debug && board && (
        <div className={styles.metaStrip}>
          scene-board · upto {board.upto ?? 0} · calls {board.calls ?? 0} · ${costUsd(board).toFixed(4)}
          {job?.status ? ` · job ${job.status}` : ""}
        </div>
      )}
    </div>
  );
});

SceneLiveView.propTypes = {
  // A live feed client (liveTap/liveFeedClient.js): the host's or a viewer's.
  feed: PropTypes.object,
  resources: PropTypes.object,
  docTitle: PropTypes.string,
  docDate: PropTypes.string,
  // The page's transcript segments and playhead — the classic opening runs
  // on them exactly as the classic system does; the turn under the
  // playhead is what the transcript panel follows.
  segments: PropTypes.array,
  currentSent: PropTypes.number,
  currentTurn: PropTypes.number,
  threshold: PropTypes.number,
  closed: PropTypes.bool,
  debug: PropTypes.bool,
  // The transcript panel beside the board: the host's toggle, and how a
  // click on the board asks for it.
  infoOpen: PropTypes.bool,
  onInfoOpenChange: PropTypes.func,
  // The board this view polls, reported up whenever it changes (the host
  // page gates its system chooser on the board's archetype).
  onBoard: PropTypes.func,
};

export default SceneLiveView;
